import { Button, Icon, LoadingPane, MenuSection, Pane } from '@folio/stripes/components';
import { IfPermission, useStripes } from '@folio/stripes/core';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import SortableMultiColumnList from '../components/SortableMultiColumnList';
import useDataRepository from '../data/useDataRepository';
import PurgeModal from '../forms/PurgeModal';
import permissions from '../types/permissions';
import { dateFromYYYYMMDD, getLocalizedDate } from '../utils/DateUtils';
import { formatList } from '../utils/I18nUtils';
import ifPermissionOr from '../utils/ifPermissionOr';
import CreateEditCalendarLayer from './CreateEditCalendarLayer';
import InfoPane from './panes/InfoPane';
var AllCalendarView = function () {
    var _a, _b;
    var intl = useIntl();
    var stripes = useStripes();
    var dataRepository = useDataRepository();
    var _c = useState(false), showPurgeModal = _c[0], setShowPurgeModal = _c[1];
    var showCreateLayerButtonRef = useRef(null);
    var history = useHistory();
    var currentRouteId = (_b = (_a = useRouteMatch('/settings/calendar/all/:calendarId')) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.calendarId;
    if (!dataRepository.isLoaded()) {
        return (React.createElement(LoadingPane, { paneTitle: intl.formatMessage({
                id: 'ui-calendar.allCalendarView.title'
            }) }));
    }
    var rows = dataRepository.getCalendars().map(function (calendar) {
        var servicePointNames = dataRepository.getServicePointNamesFromIds(calendar.assignments);
        return {
            name: calendar.name,
            startDate: getLocalizedDate(intl, calendar.startDate),
            startDateObj: dateFromYYYYMMDD(calendar.startDate),
            endDate: getLocalizedDate(intl, calendar.endDate),
            endDateObj: dateFromYYYYMMDD(calendar.endDate),
            assignments: servicePointNames.length ? (formatList(intl, servicePointNames)) : (React.createElement("div", { style: { fontStyle: 'italic', color: 'grey' } },
                React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.noAssignments" }))),
            calendar: calendar
        };
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Pane, { defaultWidth: currentRouteId === undefined ? 'fill' : '20%', paneTitle: React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.title" }), actionMenu: function (_a) {
                var onToggle = _a.onToggle;
                return ifPermissionOr(stripes, [permissions.CREATE, permissions.DELETE], React.createElement(MenuSection, { label: React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.actions.label" }) },
                    React.createElement(IfPermission, { perm: permissions.CREATE },
                        React.createElement(Button, { buttonStyle: "dropdownItem", ref: showCreateLayerButtonRef, onClick: onToggle, to: "/settings/calendar/all/create" },
                            React.createElement(Icon, { size: "small", icon: "plus-sign" },
                                React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.actions.new" })))),
                    React.createElement(IfPermission, { perm: permissions.DELETE },
                        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                                onToggle();
                                setShowPurgeModal(true);
                            } },
                            React.createElement(Icon, { size: "small", icon: "trash" },
                                React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.actions.purge" }))))));
            } },
            React.createElement(SortableMultiColumnList, { sortedColumn: "startDate", sortDirection: "ascending", dateColumns: ['startDate', 'endDate'], dateColumnMap: { startDate: 'startDateObj', endDate: 'endDateObj' }, columnMapping: {
                    name: (React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.column.name" })),
                    startDate: (React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.column.startDate" })),
                    endDate: (React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.column.endDate" })),
                    assignments: (React.createElement(FormattedMessage, { id: "ui-calendar.allCalendarView.column.assignments" }))
                }, contentData: rows, rowMetadata: ['calendar', 'startDateObj', 'endDateObj'], isSelected: function (_a) {
                    var item = _a.item;
                    return item.calendar.id === currentRouteId;
                }, onRowClick: function (_e, info) {
                    if (info.calendar.id === currentRouteId) {
                        // already displaying, being hidden
                        history.push('/settings/calendar/all/');
                    }
                    else {
                        history.push("/settings/calendar/all/".concat(info.calendar.id));
                    }
                } })),
        React.createElement(Switch, null,
            React.createElement(Route, { path: "/settings/calendar/all/create", render: function (_a) {
                    var location = _a.location;
                    return (React.createElement(IfPermission, { perm: permissions.CREATE },
                        React.createElement(CreateEditCalendarLayer, { dataRepository: dataRepository, initialValue: dataRepository.getCalendar(new URLSearchParams(location.search).get('source')), onClose: function (id) {
                                var _a;
                                if (id === void 0) { id = ''; }
                                history.push("/settings/calendar/all/".concat(id));
                                (_a = showCreateLayerButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            } })));
                } }),
            React.createElement(Route, { path: "/settings/calendar/all/edit/:id", render: function (_a) {
                    var match = _a.match;
                    return (React.createElement(IfPermission, { perm: permissions.UPDATE },
                        React.createElement(CreateEditCalendarLayer, { dataRepository: dataRepository, initialValue: dataRepository.getCalendar(match.params.id), isEdit: true, onClose: function () {
                                var _a;
                                history.push("/settings/calendar/all/".concat(match.params.id));
                                (_a = showCreateLayerButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                            } })));
                } }),
            React.createElement(Route, { path: "/settings/calendar/all/:id" },
                React.createElement(InfoPane, { editBasePath: "/settings/calendar/all/edit", creationBasePath: "/settings/calendar/all/create", onClose: function () {
                        history.push('/settings/calendar/all/');
                    }, calendar: dataRepository
                        .getCalendars()
                        .filter(function (c) { return c.id === currentRouteId; })[0], dataRepository: dataRepository }))),
        React.createElement(PurgeModal, { dataRepository: dataRepository, open: showPurgeModal, onClose: function () { return setShowPurgeModal(false); } })));
};
export default AllCalendarView;
